/**
 * QuestionTypeMenu - Google Forms-style question type dropdown
 *
 * A visual, categorized dropdown menu for selecting question types
 * with icons, descriptions, and smooth interactions.
 */
import { useState, useCallback, memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import {
  QUESTION_TYPE_CONFIG,
  QUESTION_TYPE_CATEGORY_ORDER,
  BRAND_COLOR,
} from '@/constants';
import type { QuestionType } from '@/types';

// Category labels for i18n
const categoryKeys: Record<string, string> = {
  text: 'typeCategories.text',
  choice: 'typeCategories.choice',
  scale: 'typeCategories.scale',
};

interface QuestionTypeMenuProps {
  value: string;
  questionTypes: QuestionType[];
  onChange: (typeCode: string) => void;
  disabled?: boolean;
}

export const QuestionTypeMenu = memo(function QuestionTypeMenu({
  value,
  questionTypes,
  onChange,
  disabled = false,
}: QuestionTypeMenuProps) {
  const { t } = useBuilderTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback((typeCode: string) => {
    onChange(typeCode);
    handleClose();
  }, [onChange, handleClose]);

  // Get current type config
  const currentConfig = QUESTION_TYPE_CONFIG[value] || QUESTION_TYPE_CONFIG.OPEN_TEXT;
  const CurrentIcon = currentConfig.icon;

  // Group available types by category
  // Exclude SECTION type - it's a structural element added via "Add section" button only
  const groupedTypes = questionTypes.reduce((acc, type) => {
    const config = QUESTION_TYPE_CONFIG[type.code];
    if (config && type.code !== 'SECTION') {
      const category = config.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(type);
    }
    return acc;
  }, {} as Record<string, QuestionType[]>);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={disabled}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          minWidth: 180,
          justifyContent: 'space-between',
          textTransform: 'none',
          color: 'text.primary',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 0.75,
          px: 1.5,
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: alpha(BRAND_COLOR, 0.04),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.75,
              bgcolor: alpha(currentConfig.color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CurrentIcon sx={{ fontSize: 16, color: currentConfig.color }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t(`questionTypes.${value}.name`, { defaultValue: value })}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              minWidth: 200,
              maxHeight: 360,
              mt: 0.5,
              borderRadius: 1.5,
              '& .MuiList-root': {
                py: 0.5,
              },
            },
          },
        }}
      >
        {QUESTION_TYPE_CATEGORY_ORDER.map((category, categoryIndex) => {
          const types = groupedTypes[category];
          if (!types || types.length === 0) return null;

          return (
            <Box key={category}>
              {categoryIndex > 0 && <Divider sx={{ my: 0.25 }} />}
              <ListSubheader
                sx={{
                  bgcolor: 'transparent',
                  lineHeight: 2,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: 'text.disabled',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 0,
                  minHeight: 'auto',
                }}
              >
                {t(categoryKeys[category], { defaultValue: category })}
              </ListSubheader>
              {types.map((type) => {
                const config = QUESTION_TYPE_CONFIG[type.code];
                if (!config) return null;
                const Icon = config.icon;
                const isSelected = type.code === value;

                return (
                  <MenuItem
                    key={type.code}
                    onClick={() => handleSelect(type.code)}
                    selected={isSelected}
                    sx={{
                      py: 0.625,
                      px: 1.5,
                      mx: 0.5,
                      minHeight: 36,
                      borderRadius: 0.75,
                      '&.Mui-selected': {
                        bgcolor: alpha(BRAND_COLOR, 0.1),
                        '&:hover': {
                          bgcolor: alpha(BRAND_COLOR, 0.15),
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 0.75,
                          bgcolor: alpha(config.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 15, color: config.color }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {t(`questionTypes.${type.code}.name`, { defaultValue: type.name })}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Box>
          );
        })}
      </Menu>
    </>
  );
});

export default QuestionTypeMenu;
